import { useQuery } from "@tanstack/react-query";
import {
  calculateEmployeeKpis,
  type EmployeeKpis,
} from "@/lib/employee-kpis";
import { fetchAllPaginated } from "@/lib/supabase-paginate";

interface RawProject {
  id: string;
  name: string;
  project_code: string | null;
  project_status: string | null;
  completed_at: string | null;
  created_at: string;
  is_internal: boolean | null;
  bf_actual: number | null;
  bf_estimated: number | null;
  production_lead_id: string | null;
}

interface RawTimeEntry {
  project_id: string | null;
  user_id: string | null;
  duration_seconds: number | null;
  start_date: string | null;
}

interface RawAssignment {
  user_id: string;
  hours: number | null;
  budget_lines: { project_id: string } | null;
}

interface RawProjectOwner {
  project_id: string;
  user_id: string;
  ownership_pct: number | null;
}

interface RawBudgetLine {
  project_id: string;
  quantity_hours: number | null;
}

interface RawOffer {
  project_id: string | null;
  accepted_at: string | null;
}

interface RawAbsence {
  user_id: string;
  type: string;
  status: string;
  start_date: string;
  end_date: string;
  hours: number | null;
}

/**
 * Loads the shared source data for the admin-only employee KPIs.
 * Team-wide tables are always paginated; raw Supabase selects silently stop
 * after 1000 rows and would under-report hours and GEEK shares.
 */
export function useEmployeeKpis(
  userId: string | null,
  year = new Date().getFullYear(),
  asOfDate = new Date().toISOString().slice(0, 10),
) {
  return useQuery({
    queryKey: ["employee-kpis", userId, year, asOfDate],
    enabled: !!userId,
    queryFn: async (): Promise<EmployeeKpis> => {
      const [
        projects,
        timeEntries,
        assignments,
        projectOwners,
        budgetLines,
        offers,
        absences,
      ] = await Promise.all([
        fetchAllPaginated<RawProject>(
          "projects",
          "id, name, project_code, project_status, completed_at, created_at, is_internal, bf_actual, bf_estimated, production_lead_id",
          (query) => query.is("deleted_at", null),
        ),
        fetchAllPaginated<RawTimeEntry>(
          "time_entries",
          "project_id, user_id, duration_seconds, start_date",
          (query) => query.is("deleted_at", null),
        ),
        fetchAllPaginated<RawAssignment>(
          "budget_line_assignments",
          "user_id, hours, budget_lines!inner(project_id, cost_type)",
          (query) =>
            query
              .eq("user_id", userId as string)
              .neq("budget_lines.cost_type", "external"),
        ),
        fetchAllPaginated<RawProjectOwner>(
          "project_owners",
          "project_id, user_id, ownership_pct",
          (query) => query.eq("user_id", userId as string),
        ),
        fetchAllPaginated<RawBudgetLine>(
          "budget_lines",
          "project_id, quantity_hours",
          (query) => query.eq("cost_type", "internal"),
        ),
        fetchAllPaginated<RawOffer>(
          "offers",
          "project_id, accepted_at",
          (query) => query.eq("status", "accepted").not("project_id", "is", null),
        ),
        fetchAllPaginated<RawAbsence>(
          "absences",
          "user_id, type, status, start_date, end_date, hours",
          (query) => query
            .eq("user_id", userId as string)
            .eq("type", "sick")
            .neq("status", "rejected")
            .lte("start_date", asOfDate)
            .gte("end_date", `${year}-01-01`),
        ),
      ]);

      const acceptedAtByProject = new Map<string, string>();
      for (const offer of offers) {
        if (!offer.project_id || !offer.accepted_at) continue;
        const current = acceptedAtByProject.get(offer.project_id);
        if (!current || offer.accepted_at < current) {
          acceptedAtByProject.set(offer.project_id, offer.accepted_at);
        }
      }

      return calculateEmployeeKpis({
        userId: userId as string,
        year,
        projects: projects.map((project) => ({
          id: project.id,
          name: project.name,
          projectCode: project.project_code,
          projectStatus: project.project_status,
          completedAt: project.completed_at,
          createdAt: project.created_at,
          acceptedAt: acceptedAtByProject.get(project.id) ?? null,
          isInternal: project.is_internal === true,
          bfActual: project.bf_actual,
          bfEstimated: project.bf_estimated,
          productionLeadId: project.production_lead_id,
        })),
        timeEntries: timeEntries.map((entry) => ({
          projectId: entry.project_id,
          userId: entry.user_id,
          durationSeconds: entry.duration_seconds,
          startDate: entry.start_date,
        })),
        assignments: assignments.flatMap((assignment) => {
          const projectId = assignment.budget_lines?.project_id;
          return projectId
            ? [{ userId: assignment.user_id, projectId, hours: assignment.hours }]
            : [];
        }),
        projectOwners: projectOwners.map((owner) => ({
          projectId: owner.project_id,
          userId: owner.user_id,
          ownershipPct: owner.ownership_pct,
        })),
        budgetLines: budgetLines.map((line) => ({
          projectId: line.project_id,
          quantityHours: line.quantity_hours,
        })),
        absences: absences.map((absence) => ({
          userId: absence.user_id,
          type: absence.type,
          status: absence.status,
          startDate: absence.start_date,
          endDate: absence.end_date,
          hours: absence.hours,
        })),
        asOfDate,
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}
